
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ico } from './icons';

const THEME_KEY = 'nexus_theme';
const MODES = ['light', 'dark', 'system'];

function readStoredMode() {
    try {
        const v = localStorage.getItem(THEME_KEY);
        if (v === 'light' || v === 'dark' || v === 'system') return v;
    } catch (e) { }
    return 'system';
}

function resolveTheme(mode) {
    if (mode === 'system') {
        return (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
            ? 'dark' : 'light';
    }
    return mode;
}

function applyTheme(resolved) {
    const root = document.documentElement;
    root.setAttribute('data-theme', resolved);
    let meta = document.querySelector('meta[name="theme-color"]:not([media])');
    if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'theme-color');
        document.head.appendChild(meta);
    }
    meta.setAttribute('content', resolved === 'dark' ? '#0b0e1d' : '#eef1f8');
}

const ThemeCtx = createContext({ mode: 'system', resolved: 'light', setMode: () => {} });

export function ThemeProvider({ children }) {
    const [mode, setModeState] = useState(readStoredMode);
    const [resolved, setResolved] = useState(() => {
        try {
            return resolveTheme(readStoredMode());
        } catch (e) {
            return 'light';
        }
    });

    useEffect(() => {
        const media = window.matchMedia
            ? window.matchMedia('(prefers-color-scheme: dark)')
            : null;

        const apply = () => {
            const r = resolveTheme(mode);
            setResolved(r);
            applyTheme(r);
        };

        apply();

        if (mode === 'system' && media) {
            const handler = () => apply();
            if (media.addEventListener) {
                media.addEventListener('change', handler);
                return () => media.removeEventListener('change', handler);
            }
            media.addListener(handler);
            return () => media.removeListener(handler);
        }
        return undefined;
    }, [mode]);

    const value = useMemo(() => ({
        mode,
        resolved,
        setMode: (m) => {
            if (!MODES.includes(m)) return;
            try {
                localStorage.setItem(THEME_KEY, m);
            } catch (e) { }
            setModeState(m);
        },
    }), [mode, resolved]);

    return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
    return useContext(ThemeCtx);
}

export function ThemeToggle({ compact = false }) {
    const { mode, setMode } = useTheme();
    const { t } = useTranslation();

    const order = ['light', 'system', 'dark'];
    const idx = order.indexOf(mode);
    const labels = {
        light: t('theme.light', 'Claro'),
        system: t('theme.system', 'Sistema'),
        dark: t('theme.dark', 'Oscuro'),
    };
    const icons = { light: 'sun', system: 'monitor', dark: 'moon' };

    return (
        <div
            className="theme-toggle"
            role="radiogroup"
            aria-label={t('theme.title', 'Apariencia')}
            title={t('theme.title', 'Apariencia')}
        >
            <span
                className="seg-thumb"
                style={{ transform: `translateX(${idx * 34}px)` }}
                aria-hidden="true"
            />
            {order.map((m) => (
                <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={mode === m}
                    aria-label={labels[m]}
                    title={labels[m]}
                    className={`seg-btn${mode === m ? ' active' : ''}`}
                    onClick={() => setMode(m)}
                >
                    <Ico name={icons[m]} size={15} />
                </button>
            ))}
        </div>
    );
}

export default ThemeToggle;
